import * as React from "react";
import ReactDOMServer from "react-dom/server";
import { NON_GEOCODING_PIN_SIZE } from "../Constants";

type PinType = {
  color: string;
  size: number;
  onDoubleClick?: (e: any) => void;
  opacity?: number;
  strokeWidth?: number;
  strokeColor?: string;
  // Rendered pixels per SVG unit; >1 rasterizes sharply on high-DPI screens.
  scale?: number;
};

const pinStyle = {
  cursor: "pointer",
  fill: "#d00",
  stroke: "none",
};

const ICONS = {
  SOURCE_INFO:
    "M9,6a6,6,0,0,1,6-6h0a6,6,0,0,1,6,6h0a6,6,0,0,1-6,6h0A6,6,0,0,1,9,6Zm6,6V21",
  SUGGESTED:
    "M7.5,0C5.0676,0,2.2297,1.4865,2.2297,5.2703 C2.2297,7.8378,6.2838,13.5135,7.5,15c1.0811-1.4865,5.2703-7.027,5.2703-9.7297C12.7703,1.4865,9.9324,0,7.5,0z",
  ACCEPTED:
    "M-1.547 12l6.563-6.609-1.406-1.406-5.156 5.203-2.063-2.109-1.406 1.406zM0 0q2.906 0 4.945 2.039t2.039 4.945q0 1.453-0.727 3.328t-1.758 3.516-2.039 3.070-1.711 2.273l-0.75 0.797q-0.281-0.328-0.75-0.867t-1.688-2.156-2.133-3.141-1.664-3.445-0.75-3.375q0-2.906 2.039-4.945t4.945-2.039z",
};

const SVGPathIcon = ({
  pathData,
  color = "#000000",
  strokeColor = "#000000",
  size = 24,
  opacity = 1,
  strokeWidth = 1,
  viewBox = "0 0 24 24",
}: {
  pathData: string;
  color?: string;
  strokeColor?: string;
  size?: number;
  opacity?: number;
  strokeWidth?: number;
  viewBox?: string;
}) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      width={size}
      height={size}
      fill={color}
      fillOpacity={opacity}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
    >
      <path d={pathData} />
    </svg>
  );
};

export const GET_PATH_ICON = (
  iconKey: keyof typeof ICONS,
  color = "#000000",
  size = 24,
  opacity = 1,
  strokeWidth = 1,
  viewBox: string | null | undefined = undefined,
) => {
  const pathData = ICONS[iconKey];

  if (!pathData) {
    console.error(`Icon with key "${iconKey}" not found in ICONS object`);
    return null;
  }

  let iconViewBox: string = viewBox ?? "0 0 24 24";
  if (!viewBox) {
    switch (iconKey) {
      case "SOURCE_INFO":
        iconViewBox = "0 -2 24 24";
        break;
      case "SUGGESTED":
        iconViewBox = "0 -1 15 16";
        break;
      case "ACCEPTED":
        iconViewBox = "-2 0 24 24";
        break;
      default:
        iconViewBox = "0 0 24 24";
    }
  }

  const svgString = ReactDOMServer.renderToString(
    <SVGPathIcon
      pathData={pathData}
      color={color}
      size={size}
      opacity={opacity}
      strokeWidth={strokeWidth}
      viewBox={iconViewBox}
    />,
  );

  const encodedSvg = btoa(svgString);
  return `data:image/svg+xml;base64,${encodedSvg}`;
};

export const GET_NEW_GEOCODER_PIN = (color = "#000000") => {
  const svgString = ReactDOMServer.renderToString(
    <svg
      width={32}
      height={32}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19 9.75C19 13.616 15.866 16.75 12 16.75C8.13401 16.75 5 13.616 5 9.75C5 5.88401 8.13401 2.75 12 2.75C15.866 2.75 19 5.88401 19 9.75Z"
        fill={color}
      />
      <path
        d="M12 16.75C15.866 16.75 19 13.616 19 9.75C19 5.88401 15.866 2.75 12 2.75C8.13401 2.75 5 5.88401 5 9.75C5 13.616 8.13401 16.75 12 16.75ZM12 16.75V21.25"
        stroke="#2D2E45"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
    </svg>,
  );
  const encodedSvg = btoa(svgString);
  return `data:image/svg+xml;base64,${encodedSvg}`;
};

export const GET_CIRCLE_PIN_ICON = (
  color: string,
  size: number = NON_GEOCODING_PIN_SIZE,
  opacity: number = 1,
  strokeWidth: number = 1,
  strokeColor: string = "black",
  scale: number = 1,
) => {
  const svgString = ReactDOMServer.renderToString(
    <GoogleCirclePin
      color={color}
      size={size}
      opacity={opacity}
      strokeWidth={strokeWidth}
      strokeColor={strokeColor}
      scale={scale}
    />,
  );
  const encodedSvg = btoa(svgString);

  return `data:image/svg+xml;base64,${encodedSvg}`;
};

const GoogleCirclePin = ({
  size,
  onDoubleClick,
  color = "black",
  opacity = 1,
  strokeWidth = 1,
  strokeColor = "black",
  scale = 1,
}: PinType) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size * scale}
      height={size * scale}
      viewBox={`0 0 ${size} ${size}`}
      onDoubleClick={onDoubleClick}
      style={{ ...pinStyle, fill: color }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={size / 2 - strokeWidth / 2}
        fill={color}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        opacity={opacity}
      />
    </svg>
  );
};

export default React.memo(GoogleCirclePin);

export const GET_LOW_CONFIDENCE_ICON = (color: string, strokeColor: string, scale: number = 1) => {
  const svgString = ReactDOMServer.renderToString(
    <svg
      width={26 * scale}
      height={38 * scale}
      viewBox="0 0 26 38"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1791_10630)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M13 0.5C5.8175 0.5 0 6.27328 0 13.4181C0 21.0733 5.59 23.944 9.55499 30.5784C12.09 34.8207 11.3425 37.5 13 37.5C14.7225 37.5 13.975 34.7569 16.445 30.6422C20.085 24.3586 26 21.1052 26 13.4181C26 6.27328 20.1825 0.5 13 0.5Z"
          fill={strokeColor}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M13.0167 35.5C12.7836 35.5 12.7171 35.4346 12.3176 34.225C11.9848 33.1789 11.4854 31.5769 10.1873 29.6154C8.92233 27.6866 7.59085 26.1173 6.32594 24.6135C3.36339 21.0174 1 18.2057 1 13.1385C1.03329 6.69808 6.39251 1.5 13.0167 1.5C19.6408 1.5 25 6.73078 25 13.1385C25 18.2057 22.6699 21.05 19.6741 24.6462C18.4425 26.15 17.1443 27.7193 15.8793 29.6154C14.6144 31.5442 14.0818 33.1135 13.749 34.1596C13.3495 35.4346 13.2497 35.5 13.0167 35.5Z"
          fill={color}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M13 1C5.8175 1 0 6.77328 0 13.9181C0 21.5733 5.59 24.444 9.55499 31.0784C12.09 35.3207 11.3425 38 13 38C14.7225 38 13.975 35.2569 16.445 31.1422C20.085 24.8586 26 21.6052 26 13.9181C26 6.77328 20.1825 1 13 1Z"
          fill={strokeColor}
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M13.0167 36C12.7836 36 12.7171 35.9346 12.3176 34.725C11.9848 33.6789 11.4854 32.0769 10.1873 30.1154C8.92233 28.1866 7.59085 26.6173 6.32594 25.1135C3.36339 21.5174 1 18.7057 1 13.6385C1.03329 7.19808 6.39251 2 13.0167 2C19.6408 2 25 7.23078 25 13.6385C25 18.7057 22.6699 21.55 19.6741 25.1462C18.4425 26.65 17.1443 28.2193 15.8793 30.1154C14.6144 32.0442 14.0818 33.6135 13.749 34.6596C13.3495 35.9346 13.2497 36 13.0167 36Z"
          fill={color}
        />
        <path
          d="M13 8C11.6739 8 10.4021 8.54884 9.46447 9.52577C8.52678 10.5027 8 11.8277 8 13.2093H10.5C10.5 12.5185 10.7634 11.856 11.2322 11.3675C11.7011 10.8791 12.337 10.6047 13 10.6047C13.663 10.6047 14.2989 10.8791 14.7678 11.3675C15.2366 11.856 15.5 12.5185 15.5 13.2093C15.5 15.8288 11.7571 15.5014 11.7571 19.7284H14.2571C14.2571 16.7963 18 16.4688 18 13.2093C18 11.8277 17.4732 10.5027 16.5355 9.52577C15.5979 8.54884 14.3261 8 13 8Z"
          fill="white"
        />
        <path
          d="M13.0071 21.5C13.6975 21.5 14.2571 22.0596 14.2571 22.75C14.2571 23.4404 13.6975 24 13.0071 24C12.3168 24 11.7571 23.4404 11.7571 22.75C11.7571 22.0596 12.3168 21.5 13.0071 21.5Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_1791_10630">
          <rect width="26" height="38" fill="white" />
        </clipPath>
      </defs>
    </svg>,
  );

  const encodedSvg = btoa(svgString);

  return `data:image/svg+xml;base64,${encodedSvg}`;
};

/**
 * Google's PinElement (the AdvancedMarker teardrop) as an image, for drawing pins on a canvas or GPU layer.
 * Geometry and box are copied from the `gmp-pin` Maps renders (border, background and default glyph paths;
 * 26×38 px, viewBox 0 0 26 37), so these match the DOM pins they stand in for.
 */
export const GET_TEARDROP_PIN_ICON = (
  { background, borderColor, glyphColor }: { background: string; borderColor: string; glyphColor: string },
  scale: number = 1,
) => {
  const svgString =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${26 * scale}" height="${38 * scale}" viewBox="0 0 26 37">` +
    `<g fill="none" fill-rule="evenodd">` +
    `<path fill="${borderColor}" d="M13 0C5.8175 0 0 5.77328 0 12.9181C0 20.5733 5.59 23.444 9.55499 30.0784C12.09 34.3207 11.3425 37 13 37C14.7225 37 13.975 34.2569 16.445 30.1422C20.085 23.8586 26 20.6052 26 12.9181C26 5.77328 20.1825 0 13 0Z"/>` +
    `<path fill="${background}" d="M13.0167 35C12.7836 35 12.7171 34.9346 12.3176 33.725C11.9848 32.6789 11.4854 31.0769 10.1873 29.1154C8.92233 27.1866 7.59085 25.6173 6.32594 24.1135C3.36339 20.5174 1 17.7057 1 12.6385C1.03329 6.19808 6.39251 1 13.0167 1C19.6408 1 25 6.23078 25 12.6385C25 17.7057 22.6699 20.55 19.6741 24.1462C18.4425 25.65 17.1443 27.2193 15.8793 29.1154C14.6144 31.0442 14.0818 32.6135 13.749 33.6596C13.3495 34.9346 13.2497 35 13.0167 35Z"/>` +
    `<path fill="${glyphColor}" d="M13 18C15.7614 18 18 15.7614 18 13C18 10.2386 15.7614 8 13 8C10.2386 8 8 10.2386 8 13C8 15.7614 10.2386 18 13 18Z"/>` +
    `</g></svg>`;
  return `data:image/svg+xml;base64,${btoa(svgString)}`;
};